import { useState } from 'react';
import { CONNECTION_METHODS } from '../components/AzureInstallationDialog/consts';
import { AzureDevOpsClient } from '../network/httpClients/AzureDevOpsClient/AzureDevOpsClient';
import { spawn } from '../utils/translations';

export type Phase =
  (typeof CONNECTION_METHODS)[keyof typeof CONNECTION_METHODS];

export type PATFlowState = {
  phase: Phase;
  patValue: string;
  orgUrl: string;
  error: string | null;
};

export type PATFlowHandlers = {
  onPatChange: (value: string) => void;
  onOrgUrlChange: (value: string) => void;
  onSubmit: () => Promise<{
    status: 'connected' | 'org_required';
    organizations?: any[];
  }>;
  reset: () => void;
};

const parseBackendErrorMessage = (message: string): string => {
  return message
    .replace(/^\s*\d{3}\s*:\s*/, '')
    .replace(/^\s*\[\s*\d{3}\s*\]\s*/, '')
    .replace(/\s*:\s*\d{3}\s*$/, '')
    .replace(/\s*\[\s*\d{3}\s*\]\s*$/, '')
    .trim();
};

export const usePATFlow = () => {
  const spawnPat = spawn('dialogs.azure-installation.steps.pat-connect');

  const [phase, setPhase] = useState<Phase>(CONNECTION_METHODS.PAT);
  const [patValue, setPatValue] = useState('');
  const [orgUrl, setOrgUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onPatChange = (value: string) => {
    setPatValue(value);

    if (error) setError(null);
  };

  const onOrgUrlChange = (value: string) => {
    setOrgUrl(value);

    if (error) setError(null);
  };

  const onSubmit = async () => {
    setError(null);

    if (!patValue.trim()) {
      setError(spawnPat('error-generic'));
      throw new Error('PAT value is required');
    }

    try {
      if (phase === CONNECTION_METHODS.PAT) {
        const result = await AzureDevOpsClient.connectWithPAT(patValue.trim());

        if (result.status === 'connected') {
          return {
            status: 'connected' as const,
            organizations: result.organizations,
          };
        } else {
          setPhase(CONNECTION_METHODS.ORG);

          return { status: 'org_required' as const };
        }
      } else {
        await AzureDevOpsClient.connectWithPATAndOrg(
          patValue.trim(),
          orgUrl.trim(),
        );

        return { status: 'connected' as const };
      }
    } catch (err) {
      const error = err as {
        response?: { status?: number; data?: { error?: { message?: string } } };
      };
      const status = error?.response?.status;
      const errorMessage = error?.response?.data?.error?.message;

      switch (status) {
        case 401:
          setError(spawnPat('error-invalid-pat'));
          break;
        case 403:
          setError(spawnPat('error-insufficient-permissions'));
          break;
        case 404:
          setError(spawnPat('error-org-not-found'));
          break;

        default:
          if (errorMessage) {
            const parsedMessage = parseBackendErrorMessage(errorMessage);

            setError(parsedMessage || spawnPat('error-generic'));
          } else {
            setError(spawnPat('error-generic'));
          }
      }

      throw err;
    }
  };

  const reset = () => {
    setPhase(CONNECTION_METHODS.PAT);
    setPatValue('');
    setOrgUrl('');
    setError(null);
  };

  return {
    state: {
      phase,
      patValue,
      orgUrl,
      error,
    },
    handlers: {
      onPatChange,
      onOrgUrlChange,
      onSubmit,
      reset,
      setPhase,
    },
  };
};
